"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useCart } from "@/hooks/useCart";
import { useCartUi } from "@/context/CartUiContext";
import { trackAddToCart, trackViewCart } from "@/lib/analytics";
import { products } from "@/lib/products";
import { rankUpsellSuggestions } from "@/lib/upsell";
import { formatTL } from "@/lib/format";
import { unsplash } from "@/lib/unsplash";
import { SmartImage } from "@/components/SmartImage";

/** Ücretsiz kargo eşiği — CartContext'ten (admin ayarı) gelir, hardcode YOK. */

function MinusIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className={className} aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PlusIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className={className} aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function CloseIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Mobil öncelikli sepet çekmecesi — Figma 384px sepet standardı. */
export function CartSlideOver() {
  const { open, closeCart } = useCartUi();
  const { items, count, total, discount, promoDiscount, bundle, totalAfterDiscount, updateQuantity, removeItem, freeShipThreshold: FREE_SHIPPING_LIMIT } = useCart();
  const { addItem } = useCart();
  const [addedSlug, setAddedSlug] = useState<string | null>(null);

  // Drawer açılışı = sepet görüntüleme: açılış başına bir kez view_cart
  // (sepet sayfasındaki ViewCartTracker'dan bağımsız; çift sayım yok —
  // drawer ve /sepet aynı oturumda farklı görünümlerdir).
  const viewFired = useRef(false);
  useEffect(() => {
    if (!open) {
      viewFired.current = false;
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };
    window.addEventListener("keydown", onKey);
    if (!viewFired.current && items.length > 0) {
      viewFired.current = true;
      trackViewCart(items.map((i) => ({ id: i.slug, name: i.name, price: i.price, quantity: i.quantity })));
    }
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeCart, items]);

  if (!open) return null;

  const inCart = new Set(items.map((i) => i.slug));
  // Akıllı upsell: kargo eşiğini tek ürünle kapatanlar önce (eşik aşılmışsa en ucuzlar)
  const suggestions = rankUpsellSuggestions(
    products.filter((p) => p.inStock && p.price > 0 && !inCart.has(p.slug)),
    Math.max(0, FREE_SHIPPING_LIMIT - total)
  ).map((c) => products.find((p) => p.slug === c.slug)!).filter(Boolean);

  const quickAdd = (slug: string) => {
    const p = products.find((x) => x.slug === slug);
    if (!p) return;
    addItem({ slug: p.slug, name: p.name, price: p.price, quantity: 1, image: p.image });
    trackAddToCart({ id: p.slug, name: p.name, price: p.price, quantity: 1 });
    setAddedSlug(slug);
    setTimeout(() => setAddedSlug((s) => (s === slug ? null : s)), 1500);
  };

  const remaining = Math.max(0, FREE_SHIPPING_LIMIT - total);
  const progress = Math.min(100, (total / FREE_SHIPPING_LIMIT) * 100);
  const originalOf = (slug: string) => products.find((p) => p.slug === slug)?.originalPrice;

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Sepet">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={closeCart} aria-hidden="true" />
      <aside className="absolute right-0 top-0 flex h-full w-full flex-col overflow-hidden bg-Background-light animate-slide-in-right sm:max-w-[400px] sm:rounded-l-[24px]">
        {/* Başlık */}
        <div className="flex items-center gap-2 px-4 pt-6">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-Tertiary text-xs font-bold text-White" aria-hidden="true">
            {count}
          </span>
          <h2 className="flex-1 text-base font-medium text-Black">Sepetiniz</h2>
          <button onClick={closeCart} aria-label="Sepeti kapat" className="flex h-10 w-10 items-center justify-center rounded-full text-Primary-dark transition hover:bg-White active:scale-95">
            <CloseIcon />
          </button>
        </div>

        {/* Kargo barı */}
        <div className="flex flex-col items-center gap-2 px-4 pt-4">
          <p className="text-center text-sm text-Black" aria-live="polite">
            {items.length === 0 ? "Sepetiniz boş" : remaining > 0 ? (
              <><span className="font-bold">{formatTL(remaining)}</span> daha ekleyin, <span className="font-medium">ücretsiz kargo</span> kazanın!</>
            ) : (
              <span className="font-medium">Ücretsiz kargo kazandınız!</span>
            )}
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-Background-dark/60" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label="Ücretsiz kargo ilerlemesi">
            <div className="h-1.5 rounded-full bg-Primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Ürünler + öneriler */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {items.length === 0 ? (
            <div className="rounded-[24px] bg-White p-8 text-center outline outline-1 outline-offset-[-1px] outline-Background-dark">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-Background-light outline outline-1 outline-Background-dark" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-Primary" aria-hidden="true">
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </span>
              <p className="mt-3 font-bold text-800">Sepetin boş</p>
              <p className="mt-1 text-[13px] text-500">Hemen doldurmaya başla.</p>
              <Link href="/urunler" onClick={closeCart} className="mt-4 inline-flex min-h-[44px] items-center rounded-full bg-Primary px-6 py-2.5 text-sm font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98]">
                Ürünlere Git →
              </Link>
            </div>
          ) : (
            items.map((item) => {
              const original = originalOf(item.slug);
              return (
                <article key={`${item.slug}::${item.variant ?? ""}`} className="relative rounded-[10px] bg-White p-4">
                  <button
                    onClick={() => removeItem(item.slug, item.variant)}
                    aria-label={`${item.name}${item.variant ? ` ${item.variant}` : ""} ürününü sepetten çıkar`}
                    className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-Black text-White shadow transition hover:bg-Tertiary active:scale-95"
                  >
                    <CloseIcon className="h-3.5 w-3.5" />
                  </button>
                  <div className="flex items-start gap-2">
                    <Link href={`/urunler/${item.slug}`} onClick={closeCart} className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-Background-light outline outline-1 outline-offset-[-1px] outline-Background-dark" aria-label={item.name}>
                      <SmartImage src={item.image || unsplash.nails(400, 400)} fallback={unsplash.nails(400, 400)} alt={item.name} fill className="object-cover" sizes="80px" />
                    </Link>
                    <p className="min-w-0 flex-1 text-xs uppercase leading-4 tracking-wide text-Black">
                      <Link href={`/urunler/${item.slug}`} onClick={closeCart} className="transition hover:text-Primary">
                        {item.name}
                      </Link>
                      {item.variant && (
                        <span className="mt-1 inline-block rounded-full bg-Background-light px-2 py-0.5 text-[11px] font-semibold normal-case text-Primary outline outline-1 outline-Background-dark">
                          {item.variant}
                        </span>
                      )}
                    </p>
                    <div className="flex h-20 shrink-0 flex-col items-end justify-between">
                      <p className="text-right">
                        {original && original > item.price && (
                          <span className="block text-sm font-bold leading-4 text-500 line-through">{formatTL(original)}</span>
                        )}
                        <span className="block text-lg font-bold leading-6 text-Primary">{formatTL(item.price * item.quantity)}</span>
                      </p>
                      <span className="inline-flex items-center gap-1 rounded-[70px] bg-white px-1.5 py-1 outline outline-1 outline-Primary">
                        <button onClick={() => updateQuantity(item.slug, item.quantity - 1, item.variant)} aria-label="Adedi azalt" className="flex h-8 w-8 items-center justify-center rounded-full text-Primary-dark transition hover:bg-Background-light active:scale-90">
                          <MinusIcon />
                        </button>
                        <span className="min-w-5 text-center text-sm font-medium uppercase text-Primary" aria-live="polite">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.slug, Math.min(99, item.quantity + 1), item.variant)} aria-label="Adedi artır" className="flex h-8 w-8 items-center justify-center rounded-full text-Primary-dark transition hover:bg-Background-light active:scale-90">
                          <PlusIcon />
                        </button>
                      </span>
                    </div>
                  </div>
                </article>
              );
            })
          )}

          {/* Çapraz satış */}
          {suggestions.length > 0 && (
            <section aria-label="Bunlar da ilginizi çekebilir">
              <div className="flex items-center justify-between gap-4">
                <h3 className="flex-1 text-sm uppercase leading-7 text-Black">
                  {remaining > 0 ? "Tek ürünle kargoyu bedavaya getir" : "Bunlar da ilginizi çekebilir"}
                </h3>
                <div className="flex items-center gap-1" aria-hidden="true">
                  {suggestions.map((p, i) => (
                    <span key={p.slug} className={`h-2 w-2 rounded-full ${i === 0 ? "bg-Tertiary" : "bg-Background-dark"}`} />
                  ))}
                </div>
              </div>
              <div className="mt-2 flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {suggestions.map((p) => (
                  <article key={p.slug} className="relative flex w-[220px] shrink-0 snap-start flex-col rounded-[10px] bg-White p-4 pb-14">
                    <div className="flex items-start gap-2">
                      <Link href={`/urunler/${p.slug}`} onClick={closeCart} className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-Background-light outline outline-1 outline-offset-[-1px] outline-Background-dark" aria-label={p.name}>
                        <SmartImage src={p.image} fallback={unsplash.nails(400, 400)} alt={p.name} fill className="object-cover" sizes="80px" />
                      </Link>
                      <div className="flex h-20 min-w-0 flex-1 flex-col justify-between">
                        <p className="line-clamp-3 text-xs font-medium leading-4 text-800">{p.name}</p>
                        <p className="text-sm font-bold leading-5 text-Primary">{formatTL(p.price)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => quickAdd(p.slug)}
                      className={`absolute bottom-4 right-4 rounded-[70px] px-3 py-1.5 text-xs font-medium uppercase leading-5 text-White transition active:scale-95 ${addedSlug === p.slug ? "bg-emerald-600" : "bg-Primary hover:bg-Primary-dark"}`}
                    >
                      {addedSlug === p.slug ? "Eklendi ✓" : "Sepete Ekle"}
                    </button>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Alt toplam bandı */}
        {items.length > 0 && (
          <div className="flex flex-col gap-6 rounded-t-xl bg-White p-6 shadow-[0px_-1px_10px_0px_rgba(0,0,0,0.15)] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <div className="flex flex-col gap-2.5">
              <div className="flex items-start justify-between">
                <p className="text-base font-medium leading-5 text-800">Tahmini Toplam</p>
                <p className="text-lg font-bold leading-7 text-Primary">{formatTL(totalAfterDiscount)}</p>
              </div>
              {(discount + promoDiscount + bundle.secondDiscount) > 0 && (
                <p className="text-xs font-medium text-emerald-600">
                  İndirimlerle {formatTL(discount + promoDiscount + bundle.secondDiscount)} kazandınız
                </p>
              )}
              <p className="text-sm leading-5 text-800">Kargo ücreti ödeme aşamasında hesaplanacaktır.</p>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                href="/odeme"
                onClick={closeCart}
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[70px] bg-Primary px-6 py-3 text-base font-medium uppercase leading-6 text-White transition hover:bg-Primary-dark active:scale-[0.98]"
              >
                Ödemeye Geç →
              </Link>
              <Link href="/sepet" onClick={closeCart} className="block py-1 text-center text-xs font-medium uppercase text-Primary underline-offset-2 hover:underline">
                Sepeti Görüntüle
              </Link>
              <p className="text-center text-[11px] leading-4 text-500">
                %100 orijinal ürün • 14 gün ücretsiz iade
              </p>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
